import { useToast } from '@app/core/notifications';
import { useRouter } from '@mi-front/core';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

interface DeleteButtonProps {
  readonly onDelete: () => Promise<void>;
  readonly title?: string;
  readonly message: ReactNode;
  readonly successMessage?: ReactNode;
  readonly disabled?: boolean;
  readonly redirectTo?: string;
}

export const DeleteButton = ({
  onDelete,
  title,
  message,
  successMessage,
  disabled = false,
  redirectTo,
}: DeleteButtonProps) => {
  const { formatMessage } = useIntl();
  const { notifySuccess, notifyError } = useToast();
  const { history } = useRouter();
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await onDelete();
      notifySuccess(
        successMessage || (
          <FormattedMessage
            id="common.notifications.deleted"
            defaultMessage="Eliminado correctamente"
          />
        ),
      );
      if (redirectTo) {
        history.replace(redirectTo);
      }
    } catch (err: any) {
      notifyError(
        <FormattedMessage
          id="common.notifications.deleteError"
          defaultMessage="Error al eliminar"
        />,
      );
      throw err;
    } finally {
      setIsDeleting(false);
      setOpen(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        color="error"
        startIcon={<DeleteIcon />}
        onClick={() => setOpen(true)}
        disabled={disabled}
        size="small"
      >
        {formatMessage({
          id: 'common.actions.delete',
          defaultMessage: 'Eliminar',
        })}
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm">
        {title && <DialogTitle>{title}</DialogTitle>}
        <DialogContent>
          <DialogContentText>{message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={isDeleting}>
            {formatMessage({
              id: 'common.actions.cancel',
              defaultMessage: 'Cancelar',
            })}
          </Button>
          <Button onClick={handleDelete} loading={isDeleting} color="error" variant="contained">
            {formatMessage({
              id: 'common.actions.delete',
              defaultMessage: 'Eliminar',
            })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
